import {
  AuditHistory,
  type AuditHistoryItem,
} from "@/components/molecules/audit-history";
import { useMessageHistory } from "@/features/messages/hooks/use-messages";
import { useDateFormat, useTranslation } from "@/features/utils/i18n";

/**
 * The audit trail of the request itself (`AuditHistory`, the qualifications' too):
 * who changed its status, urgency, assignee or promised date. Each line shows the
 * value of what moved and of nothing else. The discussion is the timeline, not this.
 */
export function MessageHistory({ messageId }: { messageId: string }) {
  const { t } = useTranslation("messages");
  const { format } = useDateFormat();
  const { data: history = [] } = useMessageHistory(messageId);

  const items: AuditHistoryItem[] = history.map((entry) => ({
    at: entry.operation_timestamp,
    actor: entry.actor,
    label:
      entry.operation === "UPDATE" && entry.changes.length > 0
        ? entry.changes.map((change) => t(`history.changes.${change}`)).join(", ")
        : t(`history.operations.${entry.operation}`),
    values: [
      ...(entry.values.status
        ? [t(`status.${entry.values.status}`, entry.values.status)]
        : []),
      ...(entry.values.urgency
        ? [t(`history.urgency.${entry.values.urgency}`)]
        : []),
      ...(entry.values.assignee ? [entry.values.assignee] : []),
      ...(entry.values.due_at ? [format(entry.values.due_at, "Pp")] : []),
    ],
  }));

  return (
    <AuditHistory
      items={items}
      summary={t("history.count", { count: history.length })}
      systemLabel={t("history.system")}
    />
  );
}
